// MF-554 — Card 'term-trend': the Student Summary KPI (MF-526) as a Board tile. Latest term (or
// report cycle) with data, the change vs the previous one coloured by whether it's GOOD, and the
// trend. Terms listed in options.terms keep their slot even without data (empty, never zero).
import { useEffect, useRef, useState } from 'react'
import { Box, Typography } from '@mui/material'
import MiniChart from '../../../Dashboard/StudentSummary/MiniChart'
import { SUMMARY_PALETTE, toneColor, deltaColor } from '../../../Dashboard/StudentSummary/summary.palette'
import { fmtValue } from '../boardTheme'

/** rows → [{ t, v }] (padded to the declared terms) + current/previous values. Pure, for tests. */
export const buildTrend = (rows = [], x, valueKey, terms = null) => {
  const byX = new Map(rows.map((r) => [String(r[x]), r[valueKey]]))
  const trend = (terms?.length ? terms : rows.map((r) => String(r[x]))).map((t) => {
    const v = byX.get(String(t))
    return { t: String(t), v: v === null || v === undefined ? null : Number(v) }
  })
  const withData = trend.filter((p) => p.v !== null)
  return {
    trend,
    current: withData.length ? withData[withData.length - 1].v : null,
    prev: withData.length > 1 ? withData[withData.length - 2].v : null
  }
}

/** "Term 3" → "T3" (for narrow cards, where full labels collide). */
export const shortLabel = (t) => String(t).replace(/^Term\s*/i, 'T')

/** Axis top: a fixed one if declared; integer counts get at least 0-4 so all-zero data has clean ticks. */
export const axisTop = (trend, { yMax, decimals = 0 } = {}) => {
  if (yMax) return yMax
  const max = Math.max(0, ...trend.map((p) => p.v ?? 0))
  return decimals === 0 && max < 4 ? 4 : undefined
}

/** Change vs previous, compared on the DISPLAYED value so a change that rounds to 0 is "no change". */
export const buildDelta = (current, prev, { decimals = 0, better = 'higher' } = {}) => {
  if (current === null || prev === null) return null
  const diff = current - prev
  const shown = decimals > 0 ? Math.abs(diff).toFixed(decimals) : String(Math.round(Math.abs(diff)))
  if (Number(shown) === 0) return { flat: true }
  return { value: shown, dir: diff > 0 ? 'up' : 'down', good: diff > 0 === (better !== 'lower') }
}

const TermTrendCard = ({ data }) => {
  const opts = data?.meta?.card?.options || {}
  const field = data?.encoding?.fields?.[0] || { key: 'value', unit: '' }
  const x = data?.meta?.card?.x || 'term'
  const { trend, current, prev } = buildTrend(data?.rows, x, field.key, opts.terms)
  const delta = buildDelta(current, prev, opts)
  const accent = toneColor(opts.better === 'lower' ? 'bad' : 'good')

  // The chart takes whatever height the tile leaves after the number and the delta.
  const chartRef = useRef(null)
  const [chartH, setChartH] = useState(0)
  const [chartW, setChartW] = useState(0)
  useEffect(() => {
    const el = chartRef.current
    if (!el) return undefined
    const measure = () => {
      const r = el.getBoundingClientRect()
      setChartH(Math.floor(r.height))
      setChartW(Math.floor(r.width))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 34, fontWeight: 800, lineHeight: 1, color: accent }}>
          {fmtValue(current, opts.decimals)}
          {field.unit ? (
            <Box component='span' sx={{ fontSize: 20, fontWeight: 800 }}>
              {field.unit}
            </Box>
          ) : null}
        </Typography>
        {opts.period ? (
          <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.faint }}>{opts.period}</Typography>
        ) : null}
      </Box>
      {delta ? (
        <Typography component='div' sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted, mt: 0.5 }}>
          {delta.flat ? (
            <span>– No change {String(opts.deltaText || '').replace(/^than\s+/, 'vs ')}</span>
          ) : (
            <span>
              <Box component='span' sx={{ fontWeight: 700, color: deltaColor(delta.good) }}>
                {delta.dir === 'up' ? '▲' : '▼'} {delta.value}
                {opts.deltaUnit || ''}
              </Box>{' '}
              {opts.deltaText || ''}
            </span>
          )}
        </Typography>
      ) : null}
      <Box ref={chartRef} sx={{ flex: 1, minHeight: 0, mt: 1 }}>
        {chartH >= 60 ? (
          <MiniChart
            type={opts.chart || 'line'}
            trend={
              chartW && chartW / Math.max(trend.length, 1) < 70
                ? trend.map((p) => ({ ...p, t: shortLabel(p.t) }))
                : trend
            }
            yMax={axisTop(trend, opts)}
            color={accent}
            unit={field.unit}
            height={chartH}
          />
        ) : null}
      </Box>
    </Box>
  )
}

export default TermTrendCard
